/**
 * @author: lv.chen
 * @date: 2025/8/12
 * @desc: ReportCard组件
 */
import React from 'react';
import classnames from 'classnames';
import moment from 'moment';
import { Bubble } from 'tea-component';
import { LANGUAGE_CN } from '@home/_config/config';
import { splitStringByChars } from '@home/utils/utils';
import DefaultAvatar from '@home/assets/img/common/user-default.svg';
import { getOptionText } from '@intelligence/utils/topchartsUtils';
import { getSegmentText } from '@user-center/pages/ContentManagementCenter/Reports/utils';

import './index.scss';

// @ts-ignore
interface IReportCardProps extends IBaseProps {
  locale: string;
  showCreatorInfo?: boolean;
  report: IReportPartial | null;
}

interface IReportAuthorViewModel {
  key: string;
  avatar: string;
  displayName: string;
  department: string;
}

const getLocalizedAuthorText = (locale: string, textCN?: string, textEN?: string) => {
  return locale === LANGUAGE_CN ? textCN || textEN || '' : textEN || textCN || '';
};

export const getReportPreviewAuthors = (
  report: IReportPartial | null,
  locale: string,
): IReportAuthorViewModel[] => {
  if (!report) return [];

  const primaryAuthor: IReportAuthorViewModel = {
    key: 'primary',
    avatar: report.creator_head_picture || '',
    displayName: report.creator_name_en || report.creator_name || '',
    department: getLocalizedAuthorText(
      locale,
      report.creator_department,
      report.creator_department_en,
    ),
  };

  const collaborators = Array.isArray(report.collaborators)
    ? report.collaborators
    : [];

  return [
    primaryAuthor,
    ...collaborators.slice(0, 2).map((collaborator, index) => ({
      key: `collaborator-${index}-${collaborator.open_id || 'unknown'}`,
      avatar: collaborator.creator_head_picture || '',
      displayName:
        collaborator.open_id ||
        collaborator.creator_name_en ||
        collaborator.creator_name ||
        '',
      department: getLocalizedAuthorText(
        locale,
        collaborator.creator_department,
        collaborator.creator_department_en,
      ),
    })),
  ];
};

interface IReportAuthorProps {
  author: IReportAuthorViewModel;
}

const ReportAuthor: React.FC<IReportAuthorProps> = ({ author }) => {
  const avatar = author.avatar || DefaultAvatar;
  const bubbleContent = (
    <div className="dbu-report-card__author-bubble">
      <div className="dbu-report-card__author-bubble-header">
        <img
          className="dbu-report-card__author-bubble-avatar"
          src={avatar}
          alt=""
        />
        <div className="dbu-report-card__author-bubble-name">
          {author.displayName}
        </div>
      </div>
      <div className="dbu-report-card__author-bubble-department">
        {author.department}
      </div>
    </div>
  );

  return (
    <li className="dbu-report-card__author">
      <Bubble
        dark
        placement="top"
        className="dbu-report-card__author-bubble-container"
        content={bubbleContent}
      >
        <div className="dbu-report-card__author-trigger">
          <img
            className="dbu-report-card__creator-avatar"
            src={avatar}
            alt=""
          />
          <div className="dbu-report-card__aside-content">
            <div className="dbu-report-card__creator-name">
              {author.displayName}
            </div>
            <div className="dbu-report-card__creator-department">
              {author.department}
            </div>
          </div>
        </div>
      </Bubble>
    </li>
  );
};

const ReportCard: React.FC<IReportCardProps> = (props) => {
  const { style, className, locale, showCreatorInfo = true, report } = props;

  const titleEN = report?.title_en || '';
  const titleCN = report?.title || '';
  const introductionEN = report?.introduction_en || '';
  const introductionCN = report?.introduction || '';
  const tagEN = report?.tag_en || '';
  const tagCN = report?.tag || '';
  const system = report?.system || '';
  const source = report?.source || '';

  const title = getOptionText(locale, { value: '', text: titleEN, textEN: titleEN, textCN: titleCN });
  const introduction = getOptionText(locale, {
    value: '',
    text: introductionEN,
    textEN: introductionEN,
    textCN: introductionCN,
  });
  const tag = getOptionText(locale, { value: '', text: tagEN, textEN: tagEN, textCN: tagCN });

  const tags = splitStringByChars(tag);
  const authors = getReportPreviewAuthors(report, locale);

  const showTags = Array.isArray(tags) && tags.length > 0;

  return (
    <div style={style} className={classnames('dbu-report-card', className)}>
      <div className="dbu-report-card__body">
        <img className="dbu-report-card__cover" src={report?.cover} alt="cover" />
        <div className="dbu-report-card__summary">
          <h3 className="dbu-report-card__summary-title">{title}</h3>
          <div className="dbu-report-card__summary-content">{introduction}</div>
          <div className="dbu-report-card__summary-footer">
            <div className="dbu-report-card__summary-helper-infos">
              <span>{moment().format('YYYY-MM-DD')}</span>
              <span>{getSegmentText(locale, system, source)}</span>
            </div>
            {showTags && (
              <ul className="dbu-report-card__tag-list">
                {tags.map((tag) => {
                  return (
                    <li key={tag} className="dbu-report-card__tag-item">
                      {tag}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      </div>
      {showCreatorInfo && (
        <div className="dbu-report-card__aside">
          <ul className="dbu-report-card__author-list">
            {authors.map((author) => (
              <ReportAuthor key={author.key} author={author} />
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default ReportCard;
